import { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Context from '../../Context';
import { clearCase, selectCase, selectId } from '../../../../features/case.slice';
import _axios from '../../../../utils/axios';
import { handleError } from '../../../../utils/utils';
import activeAddingEdge from '../../../../assets/arrows-alt-h-solid-30px-cultured-100.png';
import inactiveAddingEdge from '../../../../assets/arrows-alt-h-solid-30px-cultured-50.png';
import activeAnchor from '../../../../assets/anchor-solid-cultured-100.png';
import inactiveAnchor from '../../../../assets/anchor-solid-cultured-50.png';

import styles from '../../../../styles/case/Tools.module.scss';
import { ENABLE_AUTO_ASN_COUNTRY } from '../../../../utils/settings';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

const ToggleAddingEdge = styled.div`
  &&& {
    background-image: url(${props => props.addingEdge ? activeAddingEdge : inactiveAddingEdge});
  }
`;

const TogglePhysics = styled.div`
  &&& {
    background-image: url(${props => props.physics ? activeAnchor : inactiveAnchor});
  }
`;

function timeAgo(ts) {
  const now = dayjs();
  const units = ['month', 'day', 'hour', 'minute'];
  for (let unit of units) {
    if (now.diff(ts, unit) > 0) return `${now.diff(ts, unit)} ${unit}(s) ago`;
  }
  return `less than 1 minute ago`;
}

function computeUndoTitle(undoLevels) {
  if (!(undoLevels?.length > 0)) return 'No actions to undo';
  const lastLevel = undoLevels[undoLevels.length - 1];

  const opInfo = lastLevel?.operation_info || {};
  let lastLevelTooltip = 'Undo ';
  if (opInfo.op === 'delete') {
    lastLevelTooltip += `delete ${opInfo.nodeCount} node(s) and ${opInfo.edgeCount} edge(s)`
  } else if (opInfo.op === 'search') {
    lastLevelTooltip += `search results for ${opInfo.type}/${opInfo.value} from ${opInfo.ms} -> ${opInfo.fetcher}`;
  } else {
    lastLevelTooltip += (opInfo.op || 'unknown');
  }

  const timeAgoStr = lastLevel.ts ? timeAgo(lastLevel.ts) : 'unknown';
  lastLevelTooltip += `\nTimestamp: ${timeAgoStr}\nChanges:\n`;
  const ds = lastLevel?.diffSummary || {};
  if (ds.nn) lastLevelTooltip += `  - New nodes: ${ds.nn}\n`;
  if (ds.ne) lastLevelTooltip += `  - New edges: ${ds.ne}\n`;
  if (ds.mn) lastLevelTooltip += `  - Updated nodes: ${ds.mn}\n`;
  if (ds.me) lastLevelTooltip += `  - Updated edges: ${ds.me}\n`;
  if (ds.dn) lastLevelTooltip += `  - Deleted nodes: ${ds.dn}\n`;
  if (ds.de) lastLevelTooltip += `  - Deleted edges: ${ds.de}\n`;
  if (ds.nn || ds.ne || ds.mn || ds.me || ds.dn || ds.de) {} else { lastLevelTooltip += '  - no changes'; }

  return lastLevelTooltip;
}

export default function Tools(props) {
  const id = useSelector(selectId);
  const context = useContext(Context);
  const outlet = useOutlet();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const _case = useSelector(selectCase);

  // only used with setInterval to trigger redraws once every 10 seconds
  const [time, setTime] = useState(Date.now());
  const [undoTitle, setUndoTitle] = useState('No actions to undo');


  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 10000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setUndoTitle(computeUndoTitle(props.undoData));
  }, [props.undoData]);

  // function timeAxis(event) {
  //   props.setShowTimeAxisModal(true);
  // }

  function saveCase(event) {
    props.saveCase(event);
    context.setCases(cases => {
      const _case = cases.find(_case => _case.id === id);
      if (_case) {
        _case.updated_at = (new Date()).toISOString();
      }
      return [...cases];
    });
  }

  function deleteCase(event) {
    event.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete this case?\n'${props.caseMetadata.name}' (id: ${props.caseMetadata.id})`)) return;

    const config = {
      data: { id: props.caseMetadata.id }
    };
    _axios.delete(context.role.CASES_PATH, config)
      .then(response => {
        context.setCases(cases => {
          return cases.filter(_case => _case.id !== id)
        });
        dispatch(clearCase());
        navigate('/');
      })
      .catch(handleError);
  }

  return (
    <>
      <div className={styles.tools}>
        <div title='Add node' onClick={props.onAddNode}>
          <div className={styles.addNode} />
        </div>
        <div title={undoTitle} onClick={props.undo}>
          <div className={props.undoData?.length > 0 ? styles.undoActive : styles.undo} />
        </div>
        <div title='Refresh notes' onClick={() => {!props.isRefreshNotesBusy && props.refreshNotes()}}>
          <div className={props.isRefreshNotesBusy ? styles.refreshNotes : styles.refreshNotesActive} />
        </div>
        <div title='Rerun stale nodes' onClick={props.refreshStaleNodes}>
          <div className={props.hasStaleNodes ? styles.rerunStaleActive : styles.rerunStale} />
        </div>
        {/*
        <div title='Add edge' onClick={props.onAddEdge}>
          <ToggleAddingEdge className={styles.addEdge} addingEdge={props.nodeSource} />
        </div>
        */}
        {/*
        <div title='Time axis' onClick={timeAxis}>
         <div className={styles.timeAxis}/>
        </div>
        */}
        <div title='Export case' onClick={props.exportCase}>
          <div className={styles.exportCase} />
        </div>
        <div title='Save changes' onClick={saveCase}>
          <div className={props.unsavedChanges ? styles.saveCaseActive : styles.saveCase} />
        </div>
        {props.caseMetadata.id &&
          <div title='Delete case' onClick={deleteCase}>
            <div className={styles.deleteCase} />
          </div>
        }

        <div title={(props.physics ? 'Turn off' : 'Turn on') + ' the physics'} onClick={() => props.setPhysics(!props.physics)}>
          <TogglePhysics className={styles.togglePhysics} physics={props.physics} />
        </div>
        <div title='Highlight selected node neighbours' onClick={() => props.setHighlightNeighbours(!props.highlightNeighbours)}>
          <div className={props.highlightNeighbours ? styles.highlightNeighboursActive : styles.highlightNeighbours} />
        </div>
        
        { ENABLE_AUTO_ASN_COUNTRY && 
            <div title='Group IPs by Country' onClick={() => props.setFilterDefaultGrouping(v => ({...(v || {}), country: !(v?.country)}) )}>
              <div className={props.filterDefaultGrouping?.country ? styles.groupIpsByCountryActive : styles.groupIpsByCountry} />
            </div>
        }
        { ENABLE_AUTO_ASN_COUNTRY && 
          <div title='Group IPs by ASN' onClick={() => props.setFilterDefaultGrouping(v => ({...(v || {}), asn: !(v?.asn)}) )}>
              <div className={props.filterDefaultGrouping?.asn ? styles.groupIpsByAsnActive : styles.groupIpsByAsn} />
            </div>
        }
      </div>

      {outlet}
    </>
  );
}
